import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatMoney, formatDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, ExternalLink, FileSpreadsheet, FileText, Search } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

const RESULT_LABELS = {
  entregado: "Entregado",
  parcial: "Parcial",
  danado: "Dañado",
  otro_dia: "Otro día",
  no_contesto: "No contestó",
  fallido: "Fallido",
};

const RESULT_CLS = {
  entregado: "bg-emerald-100 text-emerald-900",
  parcial: "bg-yellow-100 text-yellow-900",
  danado: "bg-red-100 text-red-900",
  otro_dia: "bg-amber-100 text-amber-900",
  no_contesto: "bg-orange-100 text-orange-900",
  fallido: "bg-red-100 text-red-900",
};

function CierreDetalle({ cid, onBack }) {
  const [doc, setDoc] = useState(null);
  const [revDialog, setRevDialog] = useState({ open: false, order_id: null, numero: null, motivo: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/reporte-ruta/cierres/${cid}`).then(({ data }) => setDoc(data))
    .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const revertir = async () => {
    if (!revDialog.motivo.trim()) return toast.error("Motivo obligatorio");
    setBusy(true);
    try {
      await api.post(`/reporte-ruta/cierres/${cid}/revertir-entrega/${revDialog.order_id}`, { motivo: revDialog.motivo.trim() });
      toast.success(`Pedido ${revDialog.numero} revertido de Entregado a Pendiente`);
      setRevDialog({ open: false, order_id: null, numero: null, motivo: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!doc) return <div className="p-8 text-center text-zinc-400">Cargando cierre…</div>;

  const rows = (doc.pedidos || []).map((p) => ({
    numero: p.numero,
    vendedor: p.vendedor || "",
    cliente: p.cliente_nombre,
    telefono: p.telefono || "",
    direccion: p.direccion,
    productos: p.productos || "",
    total: p.total,
    a_cobrar: p.a_cobrar,
    resultado: RESULT_LABELS[p.resultado] || p.resultado,
    nota: p.nota,
  }));

  const columns = [
    { key: "numero", label: "N°", width: 10 },
    { key: "vendedor", label: "Vendedor", width: 16 },
    { key: "cliente", label: "Cliente", width: 22 },
    { key: "telefono", label: "Teléfono", width: 12 },
    { key: "direccion", label: "Dirección", width: 30 },
    { key: "productos", label: "Productos", width: 28 },
    { key: "total", label: "Total", width: 12, type: "money" },
    { key: "a_cobrar", label: "A cobrar", width: 12, type: "money" },
    { key: "resultado", label: "Resultado", width: 14 },
    { key: "nota", label: "Nota", width: 20 },
  ];

  const meta = {
    Código: doc.codigo,
    Fecha: doc.fecha,
    Piloto: doc.piloto_nombre || "—",
    "Cerrado por": doc.cerrado_por,
    "Total pedidos": doc.total_pedidos,
    Entregados: doc.entregados,
    "No entregados": doc.no_entregados,
  };
  const filename = `cierre-ruta-${doc.codigo}`;

  return (
    <div className="space-y-4" data-testid="cierre-detalle">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Volver al historial
        </button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportToExcel({ title: `Cierre de Ruta ${doc.codigo}`, filters: meta, columns, rows, filename })} data-testid="cierre-excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportToPdf({ title: `Cierre de Ruta ${doc.codigo}`, filters: meta, columns, rows, filename, landscape: true })} data-testid="cierre-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>
      <Card className="p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Código</div><div className="font-mono font-semibold mt-1">{doc.codigo}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Fecha</div><div className="mt-1">{formatDate(doc.fecha)}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Piloto</div><div className="mt-1">{doc.piloto_nombre || "—"}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Cerrado por</div><div className="mt-1 truncate">{doc.cerrado_por}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Total</div><div className="mt-1 font-bold">{doc.total_pedidos}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">Entregados</div><div className="mt-1 font-bold text-emerald-700">{doc.entregados}</div></div>
          <div><div className="uppercase tracking-widest text-zinc-500 text-[10px]">No entregados</div><div className="mt-1 font-bold text-orange-700">{doc.no_entregados}</div></div>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-2 py-2">N°</th>
              <th className="text-left px-2 py-2">Vendedor</th>
              <th className="text-left px-2 py-2">Cliente</th>
              <th className="text-left px-2 py-2">Teléfono</th>
              <th className="text-left px-2 py-2">Dirección</th>
              <th className="text-left px-2 py-2">Productos</th>
              <th className="text-right px-2 py-2">Total</th>
              <th className="text-right px-2 py-2">A cobrar</th>
              <th className="text-left px-2 py-2">Resultado</th>
              <th className="text-left px-2 py-2">Nota</th>
              <th className="text-center px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(doc.pedidos || []).map((p) => (
              <tr key={p.order_id} data-testid={`cierre-row-${p.numero}`} className="hover:bg-zinc-50 align-top">
                <td className="px-2 py-2 font-mono font-semibold">
                  <Link to={`/pedidos/${p.order_id}`} className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                    {p.numero} <ExternalLink className="w-3 h-3" />
                  </Link>
                </td>
                <td className="px-2 py-2 text-zinc-700">{p.vendedor || "—"}</td>
                <td className="px-2 py-2">{p.cliente_nombre}</td>
                <td className="px-2 py-2 text-zinc-600">{p.telefono || "—"}</td>
                <td className="px-2 py-2 text-zinc-600 max-w-[220px] truncate" title={p.direccion}>{p.direccion}</td>
                <td className="px-2 py-2 text-zinc-600 max-w-[180px] truncate" title={p.productos}>{p.productos || "—"}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatMoney(p.total)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-orange-700">{formatMoney(p.a_cobrar || 0)}</td>
                <td className="px-2 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${RESULT_CLS[p.resultado] || "bg-zinc-200"}`}>
                    {RESULT_LABELS[p.resultado] || p.resultado}
                  </span>
                </td>
                <td className="px-2 py-2 text-zinc-600 max-w-[180px] truncate" title={p.nota}>{p.nota || "—"}</td>
                <td className="px-2 py-2 text-center">
                  {p.resultado === "entregado" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[10px] border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => setRevDialog({ open: true, order_id: p.order_id, numero: p.numero, motivo: "" })}
                      data-testid={`revertir-${p.numero}`}
                    >
                      Revertir
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={revDialog.open} onOpenChange={(v) => setRevDialog({ ...revDialog, open: v })}>
        <DialogContent data-testid="dialog-revertir-entrega">
          <DialogHeader>
            <DialogTitle>Revertir "Entregado" · Pedido {revDialog.numero}</DialogTitle>
            <DialogDescription>
              El pedido volverá al estado <b>Pendiente</b> en Hoja de Pedidos y podrá reenviarse. Se registrará en auditoría e intentos.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Motivo (obligatorio)</Label>
            <Textarea
              rows={3}
              value={revDialog.motivo}
              onChange={(e) => setRevDialog({ ...revDialog, motivo: e.target.value })}
              autoFocus
              data-testid="input-motivo-revertir"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevDialog({ open: false, order_id: null, numero: null, motivo: "" })} disabled={busy}>Cancelar</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={revertir} disabled={!revDialog.motivo.trim() || busy} data-testid="confirm-revertir">
              {busy ? "Revirtiendo..." : "Revertir entrega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function HistorialCierresRuta() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [cierres, setCierres] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    const { data } = await api.get("/reporte-ruta/cierres", { params });
    setCierres(data.cierres || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (cid) return <CierreDetalle cid={cid} onBack={() => nav("/reporte-ruta/historial")} />;

  const filtered = cierres.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (c.codigo || "").toLowerCase().includes(s) || (c.piloto_nombre || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="historial-cierres-ruta">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Ruta</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-emerald-700" /> Historial de Cierres
          </h1>
        </div>
        <Link to="/reporte-ruta">
          <Button variant="outline" data-testid="volver-rr"><ArrowLeft className="w-4 h-4 mr-2" /> Volver a ruta</Button>
        </Link>
      </header>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="hist-desde" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="hist-hasta" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Buscar</Label>
            <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-400" />
              <Input className="pl-8" placeholder="Código, piloto…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="hist-q" />
            </div></div>
          <div className="flex items-end"><Button onClick={load} className="w-full" data-testid="hist-aplicar">Aplicar</Button></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Código</th>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Piloto</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Entregados</th>
              <th className="text-right px-3 py-2">No entregados</th>
              <th className="text-left px-3 py-2">Cerrado por</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-zinc-400">Sin cierres de ruta registrados.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-zinc-50" data-testid={`hist-row-${c.codigo}`}>
                <td className="px-3 py-2 font-mono font-semibold text-[#002FA7]">{c.codigo}</td>
                <td className="px-3 py-2">{formatDate(c.fecha)}</td>
                <td className="px-3 py-2">{c.piloto_nombre || "—"}</td>
                <td className="px-3 py-2 text-right font-semibold">{c.total_pedidos}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{c.entregados}</td>
                <td className="px-3 py-2 text-right text-orange-700 font-semibold">{c.no_entregados}</td>
                <td className="px-3 py-2 text-xs text-zinc-600">{c.cerrado_por}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/reporte-ruta/historial/${c.id}`}>
                    <Button variant="outline" size="sm" data-testid={`ver-cierre-${c.codigo}`}>Ver</Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
